import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  Page,
  LayoutSingleColumn,
  NamedLink,
  ExternalLink,
  IconArrowHead,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import FAQ_CATEGORIES from './faqData';

import css from './FAQPage.module.css';

const CTA_BUTTON_URL = '/contact';

/**
 * Single FAQ accordion item.
 * Uses a button + region pattern with `aria-expanded` and `aria-controls`
 * to keep the disclosure pattern accessible.
 */
const FAQItem = ({ questionKey, isOpen, onToggle }) => {
  const intl = useIntl();
  const questionId = `faq-question-${questionKey}`;
  const answerId = `faq-answer-${questionKey}`;

  return (
    <li className={css.faqItem}>
      <button
        type="button"
        id={questionId}
        className={css.faqQuestion}
        aria-expanded={isOpen}
        aria-controls={answerId}
        onClick={onToggle}
      >
        <span className={css.faqQuestionText}>
          {intl.formatMessage({ id: `FAQPage.qa.${questionKey}.question` })}
        </span>
        <span
          className={isOpen ? css.faqChevronOpen : css.faqChevron}
          aria-hidden="true"
        >
          <IconArrowHead direction="down" size="small" />
        </span>
      </button>
      <div
        id={answerId}
        role="region"
        aria-labelledby={questionId}
        className={isOpen ? css.faqAnswerOpen : css.faqAnswer}
      >
        <p className={css.faqAnswerText}>
          {intl.formatMessage({ id: `FAQPage.qa.${questionKey}.answer` })}
        </p>
      </div>
    </li>
  );
};

const FAQCategory = ({ categoryKey, openKey, onToggle }) => {
  const intl = useIntl();
  const category = FAQ_CATEGORIES.find(c => c.key === categoryKey);
  if (!category) return null;

  return (
    <section className={css.category} aria-labelledby={`faq-cat-${categoryKey}`}>
      <h2 id={`faq-cat-${categoryKey}`} className={css.categoryTitle}>
        {intl.formatMessage({ id: `FAQPage.category.${categoryKey}.label` })}
      </h2>
      <ul className={css.faqList}>
        {category.questions.map(qKey => (
          <FAQItem
            key={qKey}
            questionKey={qKey}
            isOpen={openKey === qKey}
            onToggle={() => onToggle(qKey)}
          />
        ))}
      </ul>
    </section>
  );
};

/**
 * Renders the CTA as a link styled like a button.
 * Uses `NamedLink` for the internal `/contact` route, a plain anchor for
 * other internal paths, and `ExternalLink` for absolute URLs.
 */
const CtaButton = ({ url, children }) => {
  if (!url) return null;
  const isInternal = url.startsWith('/');
  if (isInternal && url === '/contact') {
    return (
      <NamedLink name="ContactUsPage" className={css.ctaButton}>
        {children}
      </NamedLink>
    );
  }
  if (isInternal) {
    return (
      <a href={url} className={css.ctaButton}>
        {children}
      </a>
    );
  }
  return (
    <ExternalLink href={url} className={css.ctaButton}>
      {children}
    </ExternalLink>
  );
};

/**
 * Public-facing FAQ page.
 * Content is grouped into categories. Only one question can be expanded at a
 * time per page to keep the UI tidy.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
export const FAQPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();

  const [openKey, setOpenKey] = useState(null);
  const handleToggle = key => setOpenKey(prev => (prev === key ? null : key));

  return (
    <Page
      title={intl.formatMessage({ id: 'FAQPage.schemaTitle' })}
      description={intl.formatMessage({ id: 'FAQPage.schemaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <section className={css.hero}>
          <div className={css.heroInner}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="FAQPage.heroTitle" />
            </h1>
            <p className={css.heroSubtext}>
              <FormattedMessage id="FAQPage.heroSubtext" />
            </p>
          </div>
        </section>

        <section className={css.content}>
          <div className={css.contentInner}>
            {FAQ_CATEGORIES.map(category => (
              <FAQCategory
                key={category.key}
                categoryKey={category.key}
                openKey={openKey}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </section>

        <section className={css.ctaSection} aria-labelledby="faq-cta-headline">
          <div className={css.ctaInner}>
            <h2 id="faq-cta-headline" className={css.ctaHeadline}>
              <FormattedMessage id="FAQPage.cta.headline" />
            </h2>
            <p className={css.ctaSubtext}>
              <FormattedMessage id="FAQPage.cta.subtext" />
            </p>
            <CtaButton url={CTA_BUTTON_URL}>
              <FormattedMessage id="FAQPage.cta.buttonLabel" />
            </CtaButton>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const FAQPage = compose(connect(mapStateToProps))(FAQPageComponent);

export default FAQPage;
